import { toastOf, type MaintenanceCountdown, type WarningWords } from "../hud/maintenance-warning.ts";
import { say } from "../../words.ts";
import { QUEUE_ESTIMATE_WORDING } from "./estimate-words.ts";

export { queueEstimate } from "./estimate-words.ts";

/**
 * The queue window's state (P1.4-T164; ADR-127 decisions 5, 7, 8 and 11 and questions 8 and 9): what the page shows
 * while its `hello` waits for room, as a pure function of the `queue` frames it has seen (PROTOCOL.md 6.61.0 and
 * 6.62.0). The gateway sends `place`, the player's place counting the player, so the first in line reads 1;
 * `etaMs`, the estimated wait, or -1 while it has too few admissions to estimate from; and `length`, the entries
 * waiting. The window shows the place as its big number, the estimate in coarse words beside the character's name,
 * and a bar of the places passed against the line's length when the page joined.
 *
 * Every word is in `QUEUE_WORDING`. "Your place" and the estimate's three forms are ADR-127's; the rest are the
 * design card's, drafts for the author (ADR-127 decision 11). The card's paragraph under the bar is not shown (the
 * author's ruling of 2026-10-12).
 */

/**
 * A `queue` frame's fields.
 */
export interface QueueFrame {
  readonly place: number;
  readonly etaMs: number;
  readonly length: number;
}

/**
 * The window's state: the newest frame's fields, `start`, the length of the line the bar measures against, and
 * `at`, the page's clock when the newest frame arrived, or a maintenance schedule after it, which times the strip
 * over the window.
 */
export interface QueueState extends QueueFrame {
  readonly start: number;
  readonly at: number;
}

/**
 * The bar's value over its maximum.
 */
export interface QueueProgress {
  readonly value: number;
  readonly max: number;
}

/**
 * The window's words: its title, the label over the place, the estimate's forms, the sentence shown in the
 * estimate's place while there is none, the button and the bar's name for assistive technology.
 */
export const QUEUE_WORDING = {
  title: say("queue.title"),
  place: say("queue.place"),
  underMinute: QUEUE_ESTIMATE_WORDING.underMinute,
  overHour: QUEUE_ESTIMATE_WORDING.overHour,
  working: say("queue.working"),
  leave: say("queue.leave"),
  progress: say("queue.progress"),
} as const;

/**
 * Moves the window by one `queue` frame. The first frame fixes the bar's start at the line's length then, which for
 * a page joining at the back is its own place and for a page back at a place it held (a reload within the 2 minutes
 * the gateway keeps it, ADR-127 decision 4) counts the places ahead of it as passed. A later place past the start,
 * which a returning player put ahead of the page can cause, raises the start to it, so the bar is never below
 * nothing.
 *
 * @param state The state, or null before the first frame.
 * @param frame The frame.
 * @param now The page's clock, in epoch milliseconds.
 * @returns The next state, a new one for every frame.
 * @example
 * queueReducer(null, { place: 50, etaMs: 60_000, length: 200 }, Date.now()).start;
 * // 200
 */
export function queueReducer(state: QueueState | null, frame: QueueFrame, now: number): QueueState {
  const start = Math.max(state === null ? frame.length : state.start, frame.place);
  return { place: frame.place, etaMs: frame.etaMs, length: frame.length, start, at: now };
}

/**
 * The bar: the places passed since the start, over the start.
 *
 * @param state The state.
 * @returns The value and the maximum.
 * @example
 * queueProgress({ place: 142, etaMs: 360_000, length: 512, start: 480, at: 0 });
 * // { value: 338, max: 480 }
 */
export function queueProgress(state: QueueState): QueueProgress {
  return { value: Math.max(0, state.start - state.place), max: state.start };
}

/**
 * The strip over the window: a maintenance still to come, in the words its toast uses in the world (its time, how
 * soon and its planned length), since a player in the line should hear that the server will close (ADR-127
 * decision 8).
 *
 * @param countdown The schedule the gateway sent on this connection, or null for none.
 * @param now The page's clock, the newest frame's.
 * @param clock The time of day of a page time.
 * @returns The strip's words, or null with no schedule or once its start has passed.
 */
export function queueStrip(countdown: MaintenanceCountdown | null, now: number, clock: (ms: number) => string): WarningWords | null {
  if (countdown === null || countdown.startsAt <= now) {
    return null;
  }
  return toastOf(countdown, now, clock);
}
